import { Bounce, Power0, Power1, TimelineMax, TweenMax } from 'gsap';
import { Container } from 'pixi.js';
import { GameAudio } from '../../../Config/Audio';
import Core from '../../../Core/Core';
import { IBoardMap, TBoard } from '../../../Game/BoardArrangement';
import BoardSizing from '../../../Game/BoardSizing';
import BoardUtils, { Pair } from '../../../Game/BoardUtils';
import { Hud } from '../../../Managers/Hud/Hud';
import SoundManager from '../../../Sound/SoundManager';
import { GameContainerAction } from '../../../State/Actions/Actions';
import BoardObject from '../../../Tiles/BoardObject';
import Tile from '../../../Tiles/Tile';
import { waitForMs } from '../../../Utils/AsyncUtils';
import { getRandom, scaleToFit, shuffleArray } from '../../../Utils/Utils';
import {
    boardScore,
    firework,
    getMatchCoords,
    goldBurst,
    magnetTile,
    tileBurst,
    tileMatch,
} from '../../Animations/Animations';
import { BoardSprite, ZOOM_IN_DURATION } from './BoardSprite';
import { GoldTile } from './GoldTile';
import { ObjectSprite } from './ObjectSprite';
import { TileSprite } from './TileSprite';
import { Collection } from '../../../Tiles/TileSet';
import { Animation } from '../../Animations/Animation';
import { SkippableAnimation } from '../../Animations/SkippableAnimation';

interface ISpriteMap {
    [key: string]: BoardSprite;
}

export class BoardUI extends Container {
    public get boardSize(): [Pair, Pair] {
        return this.currentBoardSize;
    }

    public set boardSize(value: [Pair, Pair]) {
        if (Number.isNaN(value[0][0])) {
            return;
        }

        const newBoardUnitDims = BoardSizing.getFullBoardUnits(value[0]);
        const newTileSize = BoardSizing.getTileSize(this.screenSize, newBoardUnitDims);
        const currentTileSize = this.boardTileSize ? this.boardTileSize[0] : 0;
        this.tileSizeChanged = Math.abs(newTileSize[0] - currentTileSize) > Number.EPSILON;
        if (!this.tileSizeChanged) {
            return;
        }
        this.currentBoardSize = value;
        this.boardUnitDims = newBoardUnitDims;
        this.boardTileSize = newTileSize;

        if (this.board) {
            this.wallOffset = this.getBoardWallOffset(this.board, 1);
        }
    }

    public boardTileSize: Pair;

    public screenSize: Pair;
    public boardUnitDims: Pair;
    public boardLayer: PIXI.Container;
    public scatterLayer: PIXI.Container;
    public spriteMap: ISpriteMap;
    public hud: Hud;
    private board: Array<Tile | BoardObject>;
    private matchLayer: PIXI.Container;
    private matchCoords: Pair;
    private readonly activeCollection: Collection;
    private wallOffset: number = 0;
    private currentBoardSize: [Pair, Pair];
    private tileSizeChanged: boolean = true;
    private hintAnimations: Animation[] = [];
    private matchSprites: BoardSprite[] = [];

    /**
     *
     * @param screenSize - area of the screen the board can fill
     * @param boardSize - size of board in tile units [ width, height], [xOffset, yOffset]
     * and will be used to scale the board up (zoom in) when tiles are matched
     * @param hud - reference to hud for collect effects
     *
     * all positioning done here is relative to the board only
     * size and screen position of whole board is calculated and set in level view;
     */

    constructor(screenSize: Pair, boardSize: [Pair, Pair], hud: Hud, activeCollection: Collection) {
        super();

        this.hud = hud;
        this.screenSize = screenSize;
        this.boardSize = boardSize;

        this.boardLayer = new PIXI.Container();
        this.matchLayer = new PIXI.Container();
        this.scatterLayer = new PIXI.Container();
        this.addChild(this.boardLayer, this.matchLayer, this.scatterLayer);
        this.centerBoard(false);

        this.activeCollection = activeCollection;
        this.spriteMap = {};
    }

    public createTileSprites(board: Array<Tile | BoardObject>) {
        this.board = board;
        this.spriteMap = board.reduce(
            (acc, t) => {
                let s;
                if (!t.tileType) {
                    return acc;
                }
                if (t.tileType.suit === 'special') {
                    s = new ObjectSprite(this.boardTileSize, this.activeCollection);
                } else if (t.tileType.suit === 'gold') {
                    // gold tile sprite
                    s = new GoldTile(this.boardTileSize, this.activeCollection);
                } else {
                    s = new TileSprite(t.tileType, this.boardTileSize, this.activeCollection);
                }
                s.on('tileClicked', this.boardTileClicked.bind(this));
                s.setBoardId(t.boardId, t.locId);
                acc[t.boardId] = s;

                return acc;
            },
            {} as ISpriteMap
        );

        this.wallOffset = this.getBoardWallOffset(board, 1);
    }

    public async animateTiles(tiles: TBoard) {
        const animation = new SkippableAnimation();
        animation.add(() => {});
        let index = 0;
        let tileNumber = 0;
        const groupFactor = tiles.length / 20;
        for (const t of tiles) {
            const tileSprite = this.spriteMap[t.boardId];
            tileSprite.alpha = 0;
            const offset = getOffsetForLayer(t.position[0], this.boardTileSize);
            tileSprite.x =
                (t.position[1] - this.boardSize[1][0]) * this.boardTileSize[0] +
                offset +
                this.boardTileSize[0] / 2 -
                this.wallOffset;
            tileSprite.y = -this.boardLayer.y - 100;
            this.boardLayer.addChild(tileSprite);

            animation.addToLast(
                TweenMax.to(tileSprite, 0.05, {
                    delay: 0.01 * index,
                    alpha: 1,
                })
            );
            animation.addToLast(
                TweenMax.to(tileSprite, 0.15, {
                    delay: 0.01 * index,
                    y: (t.position[2] - this.boardSize[1][1]) * this.boardTileSize[1] + offset,
                    ease: Bounce.easeOut,
                })
            );
            if (++tileNumber % groupFactor) {
                index++;
            }
        }
        await animation.play();
    }

    public async failFlowAnimation() {
        const keys = Object.keys(this.spriteMap);
        shuffleArray(keys);
        let maxTime = 500;
        for (const tileId of keys) {
            const tile = this.spriteMap[tileId];
            if (!tile) {
                continue;
            }
            tile.playBlocked();
            tile.wiggle({ scale: false, repeat: 0, repeatDelay: 0, duration: 0.7 });
            const wait = getRandom(10, 20);
            maxTime -= wait;
            await waitForMs(maxTime > wait ? wait : 0);
        }
    }

    public resetDim() {
        for (const tileId in this.spriteMap) {
            const tile = this.spriteMap[tileId];
            tile.resetDim();
        }
    }

    // when placing tiles we need to convert the data positions to scaled ones
    // ie a level can have its farthest left tile be in column 3, however the fit
    // and centered board will display this in first spot

    // tiles should shift to the left by half the offset of the highest stack
    // in the farthest left column, this is the only one
    // that makes it look off center
    public displayTiles(tiles: TBoard, tween: boolean = true) {
        const updated: TileSprite[] = [];
        if (tiles.length === 0) {
            return updated;
        }

        tiles.forEach(tileInfo => {
            const tileSprite = this.spriteMap[tileInfo.boardId];

            if (!tileSprite || tileSprite.isMatched || !tileInfo.position) {
                return;
            }
            const offset = getOffsetForLayer(tileInfo.position[0], this.boardTileSize);
            const positionX =
                (tileInfo.position[1] - this.boardSize[1][0]) * this.boardTileSize[0] +
                offset +
                this.boardTileSize[0] / 2 -
                this.wallOffset;
            const positionY =
                (tileInfo.position[2] - this.boardSize[1][1]) * this.boardTileSize[1] + offset;
            TweenMax.to(tileSprite.position, tween ? ZOOM_IN_DURATION : 0.01, {
                x: positionX,
                y: positionY,
                ease: Power0.easeNone,
            });
            if (this.tileSizeChanged) {
                tileSprite.setTileSize(this.boardTileSize, tween);
            }

            if (!this.matchSprites.includes(tileSprite)) {
                this.boardLayer.addChild(tileSprite);
            }
        });

        this.tileSizeChanged = false;
        this.centerBoard();
        return updated;
    }

    public showTileMatch(matchIds: string[], map: IBoardMap) {
        const matchSprites = matchIds.map(id => this.spriteMap[map[id].boardId]);
        this.matchSprites = matchSprites;
        this.matchCoords = getMatchCoords(matchIds.map(id => map[id]), this.boardTileSize);

        // match coordinates need to adjust for offsets of current board configuration
        this.matchCoords = [
            this.matchCoords[0] - this.boardSize[1][0] * this.boardTileSize[0],
            this.matchCoords[1] -
                this.boardSize[1][1] * this.boardTileSize[1] +
                this.boardTileSize[1] * 0.5,
        ];

        tileMatch(matchSprites, this.matchCoords, this.matchLayer);

        return this.matchCoords;
    }

    public showTileBurst(
        score: number,
        soundId?: string,
        position?: [number, number],
        gold?: boolean
    ) {
        if (!this.matchCoords && !position) {
            return;
        }

        const burstPosition = position || this.matchCoords;

        if (gold) {
            goldBurst(
                this.boardTileSize,
                [burstPosition[0] + this.matchLayer.x, burstPosition[1] + this.matchLayer.y],
                this
            );
        } else {
            tileBurst(
                this.boardTileSize,
                [burstPosition[0] + this.matchLayer.x, burstPosition[1] + this.matchLayer.y],
                this
            );
        }

        if (score > 0) {
            boardScore(
                score,
                [burstPosition[0] + this.matchLayer.x, burstPosition[1] + this.matchLayer.y],
                this
            );
        }

        if (soundId) {
            SoundManager.playSound(soundId);
        }
    }

    public firecracker(id: string, score?: number) {
        const sprite = this.spriteMap[id];
        firework(
            this.boardTileSize,
            [sprite.x + this.boardLayer.x, sprite.y + this.boardLayer.y],
            this,
            true
        );

        tileBurst(
            this.boardTileSize,
            [sprite.x + this.boardLayer.x, sprite.y + this.boardLayer.y],
            this
        );

        if (score) {
            boardScore(score, [sprite.x + this.boardLayer.x, sprite.y + this.boardLayer.y], this);
        }
    }

    public magnet(magnet: string) {
        const msprite = this.spriteMap[magnet];

        magnetTile(
            this.boardTileSize,
            [msprite.x + this.boardLayer.x, msprite.y + this.boardLayer.y],
            this
        );
    }

    public pullToMagnet(id: string, magnet: string, score: number) {
        const msprite = this.spriteMap[magnet];
        const psprite = this.spriteMap[id];

        // scale up bounce psprite
        this.matchLayer.addChild(psprite);
        TweenMax.to(psprite.scale, 0.1, {
            ease: Power1.easeOut,
            repeat: 1,
            x: 1.1,
            y: 1.1,
            yoyo: true,
        });
        TweenMax.to(psprite, 0.5, {
            delay: 300 / 1000,
            ease: Power1.easeIn,
            onComplete: () => {
                if (score > 0) {
                    boardScore(
                        score,
                        [msprite.x + this.boardLayer.x, msprite.y + this.boardLayer.y],
                        this
                    );
                }

                psprite.destroy();
            },
            x: msprite.x,
            y: msprite.y,
        });
    }

    public revealMatch(ids: string[], ms: number) {
        this.stopHints();
        if (ids.length === 0) {
            return;
        }
        ids.forEach(id => {
            const sprite = this.spriteMap[id];
            const animation = sprite.wiggle({ scale: true, scaleDuration: ms });
            if (animation) {
                this.hintAnimations.push(animation);
            }
        });

        SoundManager.playSound(GameAudio.HINT);
    }

    public stopHints() {
        this.hintAnimations.forEach(x => {
            x.seek(0);
            x.kill();
        });
        this.hintAnimations = [];
    }

    public async scatterShuffled(oldSprites: BoardSprite[], newMap: IBoardMap) {
        this.boardLayer.alpha = 0;
        let mapValues = Object.values(newMap).slice(0);
        const boardWidth =
            this.boardSize[0][0] * this.boardTileSize[0] +
            this.boardSize[1][0] * this.boardTileSize[0];
        const boardHeight =
            this.boardSize[0][1] * this.boardTileSize[1] +
            this.boardSize[1][1] * this.boardTileSize[1];
        const center = {
            x: boardWidth / 2.2,
            y: boardHeight / 2.4,
        };

        const getPosition = (angle: number, radius: number, rotation: number = 0) => {
            return {
                x: center.x + radius * Math.cos(angle),
                y: center.y + radius * Math.sin(angle),
                rotation,
            };
        };

        return new Promise(resolve => {
            oldSprites.forEach((sprite, index) => {
                const radius = getRandom(50, 200);
                const duration = 0.4;

                const angle = getRandom(0, 2 * Math.PI);
                const startPosition = getPosition(angle, radius);
                const targetPositions = [];

                for (let i = 0; i < 5; i++) {
                    const rotation = i * 90;
                    targetPositions.push(getPosition(angle + rotation, radius, 3 * rotation));
                }

                const timeLine = new TimelineMax({
                    onComplete: () => {
                        if (index === oldSprites.length - 1) {
                            this.scatterLayer.removeChildren();
                            this.displayTiles(Core.store.getState().StateBoard.boardTiles, false);
                            this.boardLayer.alpha = 1;
                            this.centerBoard();
                            resolve();
                        }
                    },
                });
                timeLine.to(sprite, 0.1, {
                    x: startPosition.x,
                    y: startPosition.y,
                    delay: 0.3,
                    ease: Power1.easeOut,
                });
                timeLine.to(sprite, duration, {
                    bezier: {
                        curviness: 1.5,
                        values: targetPositions,
                    },
                    ease: Power0.easeNone,
                    repeat: 2,
                });
                // find id of matching value in new board...
                const matchFind = mapValues.find(
                    v => BoardUtils.formatId(v.tileType) === sprite.spriteId
                );
                if (matchFind) {
                    mapValues = mapValues.filter(f => f !== matchFind);
                    const matchId = matchFind.boardId;
                    timeLine.to(
                        sprite,
                        0.2,
                        {
                            ease: Power1.easeInOut,
                            x:
                                (newMap[matchId].position[1] - this.boardSize[1][0]) *
                                    this.boardTileSize[0] +
                                this.boardTileSize[0] / 2,
                            y:
                                (newMap[matchId].position[2] - this.boardSize[1][1]) *
                                this.boardTileSize[1],
                        },
                        '-=' + 0.5 * duration
                    );
                }
            });
        });
    }

    public dimAllTiles(ms: number) {
        Object.values(this.spriteMap).forEach(s => {
            // @ts-ignore
            s.dim(ms);
        });
    }

    public revealAvailable(ms: number) {
        Object.values(this.spriteMap).forEach(s => {
            if (s.isAvailable) {
                // @ts-ignore
                s.revealAvailable(ms);
            }
        });
    }

    public async collectBoardObjects(found: string[], map: IBoardMap) {
        if (!this.hud.view.topUi) {
            return;
        }
        const goalIcon = this.hud.view.topUi.getGoalDisplay().getGoalIcon();
        const targetPosition = this.hud.view.toLocal(goalIcon.position, goalIcon.parent);
        found.forEach(s => {
            if (map[s]) {
                const sprite = this.spriteMap[s];
                if (sprite.isMatched) {
                    return;
                }
                TweenMax.killTweensOf(sprite.position);
                TweenMax.killTweensOf(sprite.scale);
                const targetScale = scaleToFit(
                    sprite.width,
                    sprite.height,
                    goalIcon.width,
                    goalIcon.height
                );
                sprite.isMatched = true;
                sprite.anchor.set(0.5);

                const oldPosition = this.hud.view.toLocal(sprite.position, sprite.parent);
                sprite.position = oldPosition;
                sprite.setParent(this.hud.view);

                const animation = new Animation();
                animation.add(
                    TweenMax.to(sprite.scale, 0.2, {
                        ease: Power0.easeIn,
                        x: sprite.scale.x * 1.2,
                        y: sprite.scale.y * 1.2,
                    })
                );
                animation.add(
                    TweenMax.to(sprite, 0.1, {
                        rotation: 0.2,
                        yoyo: true,
                        repeat: 2,
                    })
                );
                animation.add(
                    TweenMax.to(sprite, 0.1, {
                        rotation: 0,
                    })
                );
                animation.add(
                    TweenMax.to(sprite, 0.3, {
                        ease: Power0.easeInOut,
                        x: targetPosition.x,
                        y: targetPosition.y,
                    })
                );
                animation.add(
                    TweenMax.to(sprite.scale, 0.2, {
                        ease: Power0.easeOut,
                        onComplete: () => {
                            sprite.destroy();
                        },
                        x: targetScale,
                        y: targetScale,
                    })
                );
                animation.play();
            }
        });
    }

    public showTileType(visible: boolean) {
        // tslint:disable-next-line:forin
        for (const spriteMapKey in this.spriteMap) {
            const sprite = this.spriteMap[spriteMapKey];
            sprite.setTypeVisible(visible);
        }
    }

    public cleanUpForRetry() {
        this.boardLayer.removeChildren();
        this.matchLayer.removeChildren();
        this.scatterLayer.removeChildren();

        Object.keys(this.spriteMap).forEach(key => {
            delete this.spriteMap[key];
        });
    }

    public clearHighlight() {
        // tslint:disable-next-line:forin
        for (const key in this.spriteMap) {
            const sprite = this.spriteMap[key];
            sprite.setAvailable(true, true);
        }
    }

    /**
     * finds offset to shift board so that a high stack of tiles does not make the board look off center
     * @param tiles - tile data array
     * @param index - position index to check, 1 for x (left side), 2 for y (top)
     */
    private getBoardWallOffset(tiles: TBoard, index: number) {
        const sorted = tiles.slice(0);
        const farthest = sorted.sort((a, b) => a.position[index] - b.position[1])[0].position[
            index
        ];
        const wallColumn = sorted.filter(t => t.position[index] === farthest);
        const wall = wallColumn.sort((a, b) => a.position[0] - b.position[0]).pop().position[0];
        return getOffsetForLayer(wall, this.boardTileSize) / 2;
    }

    // center current layout within full board size
    private centerBoard(tween: boolean = true) {
        const offsetXUnits = (this.boardUnitDims[0] - this.boardSize[0][0]) / 2;
        const offsetXPixels = offsetXUnits * this.boardTileSize[0];
        const offsetYUnits = (this.boardUnitDims[1] - this.boardSize[0][1]) / 2;
        const offsetYPixels = offsetYUnits * this.boardTileSize[0];
        TweenMax.to(
            [this.boardLayer, this.matchLayer, this.scatterLayer],
            tween ? ZOOM_IN_DURATION : 0.01,
            {
                x: offsetXPixels,
                y: offsetYPixels,
                ease: Power0.easeNone,
            }
        );
    }

    private boardTileClicked(id: string) {
        this.emit(GameContainerAction.TILE_SELECTED, id);
    }
}

const getOffsetForLayer = (layer: number, tileSize: [number, number]) => {
    return -(tileSize[0] * 0.1) * layer;
};
